import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { BookOpen, ChevronDown, ClipboardList, FileCheck, GraduationCap, ShieldCheck, Sparkles, Video } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { formatTrainingModuleOptionLabel, formatTrainingModuleTitle, getDefaultSectionForViewer, isExpired, readSectionFromPath, toDateInput, type TrainingSection } from './utils'
import { AssignmentsSection, LearnerSection, LibrarySection, OverviewSection, ReviewSection, TracksSection, TranscriptSection } from './sections'
import type { ModuleContentBlockDraft, QuizAttemptEvidenceRow, WorkspaceData } from './types'
import { getAdminUsers, type AdminUserSummary } from '../../lib/services/adminUsers'
import {
  approveReviewerQueueItem,
  approveRecommendation,
  createTrainingTrackDeployment,
  createCompletion,
  createTrainingAssignment,
  createTrainingModule,
  generateEvidencePackage,
  generateRecommendationDraft,
  getEvidencePackage,
  getTrainingAdminOverview,
  getTrainingModuleDetail,
  getTrainingTrackDetail,
  getTranscript,
  getVideoSummary,
  generateQuizDraft,
  getQuizPlayerData,
  linkRecommendation,
  listApprovedQuizzes,
  listCompetencyValidations,
  listReviewerQueue,
  listRecommendationDrafts,
  listTrainingAssignments,
  listTrainingModules,
  listTrainingTracks,
  recordVideoEvent,
  rejectReviewerQueueItem,
  requestCompetencyValidation,
  requestTrainingExport,
  reviewCompetencyValidation,
  submitQuizAttempt,
  type CompletionTranscriptRow,
  type QuizAttemptResult,
  type QuizReviewItem,
  type RecommendationDraft,
  type TrainingAssignment,
  type TrainingCompetencyValidation,
  type TrainingEvidencePackage,
  type TrainingModule,
  type TrainingModuleDetail,
  type TrainingReviewerQueueItem,
  type TrainingTrack,
  type TrainingTrackDetail,
  type TrainingVideoSummary,
} from '../../lib/services/training'
import { canShowAnes008MediaProof } from './stagedMediaAccess'

function useTrainingWorkspaceData(section: TrainingSection, viewerRole: string | undefined): WorkspaceData {
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [modules, setModules] = useState<TrainingModule[]>([])
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([])
  const [reviewQueue, setReviewQueue] = useState<TrainingReviewerQueueItem[]>([])
  const [recommendations, setRecommendations] = useState<RecommendationDraft[]>([])
  const [competencyValidations, setCompetencyValidations] = useState<TrainingCompetencyValidation[]>([])
  const [tracks, setTracks] = useState<TrainingTrack[]>([])
  const [selectedTrackId, setSelectedTrackId] = useState('')
  const [selectedTrackDetail, setSelectedTrackDetail] = useState<TrainingTrackDetail | null>(null)
  const [approvedQuizzes, setApprovedQuizzes] = useState<QuizReviewItem[]>([])
  const [transcript, setTranscript] = useState<CompletionTranscriptRow[]>([])
  const [adminOverview, setAdminOverview] = useState<{ module_count: number; assignment_count: number; completion_count: number; expiring_within_30d_count: number } | null>(null)
  const [videoSummary, setVideoSummary] = useState<TrainingVideoSummary | null>(null)
  const [moduleTitle, setModuleTitle] = useState('')
  const [moduleCode, setModuleCode] = useState('')
  const [moduleStandards, setModuleStandards] = useState('')
  const [moduleDomainTags, setModuleDomainTags] = useState('')
  const [moduleCadenceTags, setModuleCadenceTags] = useState('annual')
  const [moduleRoleApplicability, setModuleRoleApplicability] = useState('universal')
  const [moduleTrackIds, setModuleTrackIds] = useState('')
  const [moduleRecommendationTypes, setModuleRecommendationTypes] = useState('training_only')
  const [modulePolicyRefs, setModulePolicyRefs] = useState('')
  const [moduleLearningDepth, setModuleLearningDepth] = useState<'awareness' | 'knowledge_attestation' | 'skill_demonstration' | 'validated_competency'>('knowledge_attestation')
  const [moduleDurationMinutes, setModuleDurationMinutes] = useState(15)
  const [moduleAuthorName, setModuleAuthorName] = useState('')
  const [moduleReviewerName, setModuleReviewerName] = useState('')
  const [moduleApproverName, setModuleApproverName] = useState('')
  const [moduleClinicalAccuracyScope, setModuleClinicalAccuracyScope] = useState('')
  const [moduleLastReviewedAt, setModuleLastReviewedAt] = useState(toDateInput(new Date().toISOString()))
  const [moduleNextReviewDueAt, setModuleNextReviewDueAt] = useState(toDateInput(new Date(Date.now() + 365 * 86400000).toISOString()))
  const [moduleBlocks, setModuleBlocks] = useState<ModuleContentBlockDraft[]>([{ type: 'text', text: '' }])
  const [moduleExpiryDays, setModuleExpiryDays] = useState(365)
  const [quizPrompt, setQuizPrompt] = useState('Generate a baseline compliance quiz from the selected module.')
  const [recommendationPrompt, setRecommendationPrompt] = useState('Recommend standards-aligned question focus areas for this module.')
  const [recommendationStandards, setRecommendationStandards] = useState('')
  const [selectedModuleId, setSelectedModuleId] = useState('')
  const [selectedModuleDetail, setSelectedModuleDetail] = useState<TrainingModuleDetail | null>(null)
  const [assignmentModuleId, setAssignmentModuleId] = useState('')
  const [assignmentScope, setAssignmentScope] = useState<'facility' | 'role' | 'individual'>('role')
  const [assignmentDueDate, setAssignmentDueDate] = useState(toDateInput(new Date(Date.now() + 7 * 86400000).toISOString()))
  const [assignmentTargetRole, setAssignmentTargetRole] = useState('user')
  const [assignmentAssignee, setAssignmentAssignee] = useState('')
  const [assignmentUserOptions, setAssignmentUserOptions] = useState<AdminUserSummary[]>([])
  const [trackDeploymentScope, setTrackDeploymentScope] = useState<'facility' | 'role' | 'individual'>('role')
  const [trackDeploymentDueDate, setTrackDeploymentDueDate] = useState(toDateInput(new Date(Date.now() + 14 * 86400000).toISOString()))
  const [trackDeploymentFacilityId, setTrackDeploymentFacilityId] = useState('')
  const [trackDeploymentTargetRole, setTrackDeploymentTargetRole] = useState('user')
  const [trackDeploymentAssignee, setTrackDeploymentAssignee] = useState('')
  const [trackDeploymentNotes, setTrackDeploymentNotes] = useState('')
  const [selectedQuizId, setSelectedQuizId] = useState('')
  const [quizQuestionIndex, setQuizQuestionIndex] = useState(0)
  const [quizAnswers, setQuizAnswers] = useState<Array<number | null>>([])
  const [quizPlayerData, setQuizPlayerData] = useState<Awaited<ReturnType<typeof getQuizPlayerData>> | null>(null)
  const [lastAttemptResult, setLastAttemptResult] = useState<QuizAttemptResult | null>(null)
  const [attemptHistory, setAttemptHistory] = useState<QuizAttemptEvidenceRow[]>([])
  const [quizLoadError, setQuizLoadError] = useState<string | null>(null)
  const [reviewDecisionComments, setReviewDecisionComments] = useState<Record<string, string>>({})
  const [reviewDecisionAction, setReviewDecisionAction] = useState<Record<string, 'approved' | 'rejected'>>({})
  const [transcriptStatusFilter, setTranscriptStatusFilter] = useState<'completed' | 'open' | 'overdue'>('completed')
  const [transcriptModuleFilter, setTranscriptModuleFilter] = useState('')
  const [transcriptTrackFilter, setTranscriptTrackFilter] = useState('')
  const [transcriptDeploymentFilter, setTranscriptDeploymentFilter] = useState('')
  const [dashboardFacilityFilter, setDashboardFacilityFilter] = useState('')
  const [dashboardRoleFilter, setDashboardRoleFilter] = useState('')
  const [selectedEvidencePackage, setSelectedEvidencePackage] = useState<TrainingEvidencePackage | null>(null)
  const [isEvidenceLoading, setIsEvidenceLoading] = useState(false)
  const [learnerAttestations, setLearnerAttestations] = useState<Record<string, boolean>>({})
  const [librarySearch, setLibrarySearch] = useState('')
  const [libraryStatusFilter, setLibraryStatusFilter] = useState<'' | 'draft' | 'approved' | 'retired'>('')
  const [libraryDepthFilter, setLibraryDepthFilter] = useState<'' | 'awareness' | 'knowledge_attestation' | 'skill_demonstration' | 'validated_competency'>('')
  const [libraryDomainFilter, setLibraryDomainFilter] = useState('')
  const [libraryTrackFilter, setLibraryTrackFilter] = useState('')
  const [recommendationDueDates, setRecommendationDueDates] = useState<Record<string, string>>({})
  const [recommendationRemediationStrengths, setRecommendationRemediationStrengths] = useState<Record<string, 'training_only' | 'training_plus_checklist' | 'training_plus_system_change'>>({})
  const [competencyReviewNotes, setCompetencyReviewNotes] = useState<Record<string, string>>({})

  const loadAllData = useCallback(async () => {
    setError(null)
    setIsLoading(true)
    try {
      const needsModules = section === 'overview' || section === 'library' || section === 'tracks' || viewerRole === 'Admin'
      const needsAssignments = section === 'overview' || section === 'assignments' || section === 'learner' || viewerRole === 'Admin' || viewerRole === 'Reviewer'
      const needsReview = section === 'review' || viewerRole === 'Reviewer' || viewerRole === 'Admin'
      const needsApprovedQuizzes = section === 'learner'
      const needsTranscript = section === 'transcript' || section === 'overview' || section === 'learner'
      const needsTracks = section === 'overview' || section === 'library' || section === 'tracks'
      const needsOverview = section === 'overview'
      const needsVideo = section === 'overview'
      const needsAssignmentUsers = (section === 'assignments' || section === 'transcript') && viewerRole === 'Admin'
      const needsCompetency = needsReview || section === 'learner' || section === 'transcript' || needsOverview

      const [moduleResult, assignmentResult, reviewResult, approvedResult, transcriptResult, recommendationResult, competencyResult, trackResult, overviewResult, videoResult, userResult] = await Promise.allSettled([
        needsModules ? listTrainingModules() : Promise.resolve(null),
        needsAssignments ? listTrainingAssignments() : Promise.resolve(null),
        needsReview ? listReviewerQueue() : Promise.resolve(null),
        needsApprovedQuizzes ? listApprovedQuizzes() : Promise.resolve(null),
        needsTranscript ? getTranscript() : Promise.resolve(null),
        needsReview ? listRecommendationDrafts() : Promise.resolve(null),
        needsCompetency ? listCompetencyValidations() : Promise.resolve(null),
        needsTracks ? listTrainingTracks() : Promise.resolve(null),
        needsOverview ? getTrainingAdminOverview() : Promise.resolve(null),
        needsVideo ? getVideoSummary() : Promise.resolve(null),
        needsAssignmentUsers ? getAdminUsers() : Promise.resolve(null),
      ])
      const moduleRows = moduleResult.status === 'fulfilled' ? moduleResult.value : []
      const assignmentRows = assignmentResult.status === 'fulfilled' ? assignmentResult.value : []
      const reviewRows = reviewResult.status === 'fulfilled' ? reviewResult.value : []
      const approvedRows = approvedResult.status === 'fulfilled' ? approvedResult.value : []
      const transcriptRows = transcriptResult.status === 'fulfilled' ? transcriptResult.value : []
      const recommendationRows = recommendationResult.status === 'fulfilled' ? recommendationResult.value : []
      const competencyRows = competencyResult.status === 'fulfilled' ? competencyResult.value : []
      const trackRows = trackResult.status === 'fulfilled' ? trackResult.value : []
      const overviewRows = overviewResult.status === 'fulfilled' ? overviewResult.value : null
      const videoRows = videoResult.status === 'fulfilled' ? videoResult.value : null
      const userRows = userResult.status === 'fulfilled' ? userResult.value : []

      const attemptedResults: PromiseSettledResult<unknown>[] = []
      if (needsModules) attemptedResults.push(moduleResult)
      if (needsAssignments) attemptedResults.push(assignmentResult)
      if (needsReview) attemptedResults.push(reviewResult)
      if (needsApprovedQuizzes) attemptedResults.push(approvedResult)
      if (needsTranscript) attemptedResults.push(transcriptResult)
      if (needsReview) attemptedResults.push(recommendationResult)
      if (needsCompetency) attemptedResults.push(competencyResult)
      if (needsTracks) attemptedResults.push(trackResult)
      if (needsOverview) attemptedResults.push(overviewResult)
      if (needsVideo) attemptedResults.push(videoResult)
      if (needsAssignmentUsers) attemptedResults.push(userResult)

      const rejectedCallCount = attemptedResults.filter((result) => result.status === 'rejected').length
      if (attemptedResults.length > 0 && rejectedCallCount === attemptedResults.length) {
        setError('Failed to load training data. Please retry.')
      }

      if (moduleRows) setModules(moduleRows)
      if (assignmentRows) setAssignments(assignmentRows)
      if (reviewRows) setReviewQueue(reviewRows)
      if (approvedRows) setApprovedQuizzes(approvedRows)
      if (transcriptRows) setTranscript(transcriptRows)
      if (recommendationRows) setRecommendations(recommendationRows)
      if (competencyRows) setCompetencyValidations(competencyRows)
      if (trackRows) setTracks(trackRows)
      if (overviewRows) setAdminOverview(overviewRows)
      if (videoRows) setVideoSummary(videoRows)
      if (userRows) setAssignmentUserOptions(userRows)

      if (moduleRows && moduleRows.length > 0) {
        const firstModuleId = moduleRows[0].module_id
        setSelectedModuleId((current) => current || firstModuleId)
        setAssignmentModuleId((current) => current || firstModuleId)
      }
      if (approvedRows && approvedRows.length > 0) {
        const firstQuizId = approvedRows[0].quiz_id
        setSelectedQuizId((current) => current || firstQuizId)
      }
      if (trackRows && trackRows.length > 0) {
        const firstTrackId = trackRows[0].track_id
        setSelectedTrackId((current) => current || firstTrackId)
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load training data')
    } finally {
      setIsLoading(false)
    }
  }, [section, viewerRole])

  useEffect(() => {
    void loadAllData()
  }, [loadAllData])

  useEffect(() => {
    if (!selectedModuleId) {
      setSelectedModuleDetail(null)
      return
    }
    let cancelled = false
    getTrainingModuleDetail(selectedModuleId)
      .then((moduleDetail) => {
        if (!cancelled) setSelectedModuleDetail(moduleDetail)
      })
      .catch(() => {
        if (!cancelled) setSelectedModuleDetail(null)
      })
    return () => {
      cancelled = true
    }
  }, [selectedModuleId])

  useEffect(() => {
    if (!selectedTrackId) {
      setSelectedTrackDetail(null)
      return
    }
    let cancelled = false
    getTrainingTrackDetail(selectedTrackId)
      .then((trackDetail) => {
        if (!cancelled) setSelectedTrackDetail(trackDetail)
      })
      .catch(() => {
        if (!cancelled) setSelectedTrackDetail(null)
      })
    return () => {
      cancelled = true
    }
  }, [selectedTrackId])

  useEffect(() => {
    if (!selectedQuizId) {
      setQuizPlayerData(null)
      setLastAttemptResult(null)
      setQuizLoadError(null)
      return
    }
    let cancelled = false
    setQuizLoadError(null)
    getQuizPlayerData(selectedQuizId)
      .then((payload) => {
        if (cancelled) return
        setQuizPlayerData(payload)
        setQuizQuestionIndex(0)
        setQuizAnswers(new Array(payload.questions.length).fill(null))
        setLastAttemptResult(null)
        if (payload.quiz.module_id) {
          void recordVideoEvent(payload.quiz.module_id, 'view_started').catch(() => undefined)
        }
      })
      .catch((loadError) => {
        if (cancelled) return
        setQuizLoadError(loadError instanceof Error ? loadError.message : 'Failed to load selected quiz')
      })
    return () => {
      cancelled = true
    }
  }, [selectedQuizId])

  const moduleOptions = useMemo(
    () => modules.map((moduleRow) => ({ value: moduleRow.module_id, label: formatTrainingModuleOptionLabel(moduleRow) })),
    [modules],
  )

  const libraryDomainOptions = useMemo(() => {
    const values = new Set<string>()
    for (const moduleRow of modules) {
      for (const tag of moduleRow.domain_tags || []) if (tag) values.add(tag)
    }
    return Array.from(values).sort((a, b) => a.localeCompare(b))
  }, [modules])

  const libraryTrackOptions = useMemo(() => {
    if (tracks.length > 0) return tracks.map((track) => track.track_key)
    const values = new Set<string>()
    for (const moduleRow of modules) {
      for (const trackId of moduleRow.related_track_ids || []) if (trackId) values.add(trackId)
    }
    return Array.from(values).sort((a, b) => a.localeCompare(b))
  }, [modules, tracks])

  const filteredModules = useMemo(() => modules.filter((moduleRow) => {
    const haystack = [
      moduleRow.title,
      formatTrainingModuleTitle(moduleRow),
      moduleRow.module_code,
      moduleRow.package_version_key,
      ...(moduleRow.domain_tags || []),
      ...(moduleRow.related_track_ids || []),
      ...(moduleRow.mapped_standard_ids || []),
    ].filter(Boolean).join(' ').toLowerCase()
    const searchMatch = !librarySearch.trim() || haystack.includes(librarySearch.trim().toLowerCase())
    const statusMatch = !libraryStatusFilter || moduleRow.status === libraryStatusFilter
    const depthMatch = !libraryDepthFilter || moduleRow.learning_depth === libraryDepthFilter
    const domainMatch = !libraryDomainFilter || (moduleRow.domain_tags || []).includes(libraryDomainFilter)
    const trackMatch = !libraryTrackFilter || (moduleRow.related_track_ids || []).includes(libraryTrackFilter)
    return searchMatch && statusMatch && depthMatch && domainMatch && trackMatch
  }), [libraryDepthFilter, libraryDomainFilter, librarySearch, libraryStatusFilter, libraryTrackFilter, modules])

  const libraryTrackSummary = useMemo(() => {
    const counts = new Map<string, number>()
    for (const moduleRow of filteredModules) {
      for (const trackId of moduleRow.related_track_ids || []) counts.set(trackId, (counts.get(trackId) || 0) + 1)
    }
    if (tracks.length > 0) {
      return tracks
        .map((track) => ({ trackId: track.title, moduleCount: counts.get(track.track_key) ?? 0 }))
        .filter((track) => track.moduleCount > 0)
        .sort((a, b) => a.trackId.localeCompare(b.trackId))
    }
    return Array.from(counts.entries()).map(([trackId, moduleCount]) => ({ trackId, moduleCount })).sort((a, b) => a.trackId.localeCompare(b.trackId))
  }, [filteredModules, tracks])

  async function handleCreateModule() {
    if (!moduleTitle.trim()) {
      setError('Module title is required')
      return
    }
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      const contentBlocks = moduleBlocks.map((block) => ({ type: block.type, text: block.text.trim() })).filter((block) => block.text.length > 0)
      if (contentBlocks.length === 0) {
        setError('Add at least one structured content block')
        return
      }
      const phiLikePattern = /(ssn|social security|mrn|medical record|date of birth|patient name|patient id)/i
      if (contentBlocks.some((block) => phiLikePattern.test(block.text))) {
        setError('Remove PHI-like content from module blocks before saving')
        return
      }
      await createTrainingModule({
        title: moduleTitle.trim(),
        module_code: moduleCode.trim() || undefined,
        content_blocks: contentBlocks,
        mapped_standard_ids: moduleStandards.split(',').map((token) => token.trim()).filter(Boolean),
        expiration_interval_days: moduleExpiryDays,
        duration_minutes: moduleDurationMinutes,
        domain_tags: moduleDomainTags.split(',').map((token) => token.trim()).filter(Boolean),
        learning_depth: moduleLearningDepth,
        cadence_tags: moduleCadenceTags.split(',').map((token) => token.trim()).filter(Boolean),
        role_applicability: moduleRoleApplicability.split(',').map((token) => token.trim()).filter(Boolean),
        related_track_ids: moduleTrackIds.split(',').map((token) => token.trim()).filter(Boolean),
        related_recommendation_types: moduleRecommendationTypes.split(',').map((token) => token.trim()).filter(Boolean),
        linked_policy_refs: modulePolicyRefs.split(',').map((token) => token.trim()).filter(Boolean),
        trust_signal_actors: {
          author: { name: moduleAuthorName.trim() || 'Pending author', role: 'author', status: moduleAuthorName.trim() ? 'approved' : 'pending' },
          reviewer: { name: moduleReviewerName.trim() || 'Pending reviewer', role: 'reviewer', status: moduleReviewerName.trim() ? 'approved' : 'pending' },
          approver: { name: moduleApproverName.trim() || 'Pending approver', role: 'approver', status: moduleApproverName.trim() ? 'approved' : 'pending' },
        },
        clinical_accuracy_scope: moduleClinicalAccuracyScope.trim() || undefined,
        last_reviewed_at: moduleLastReviewedAt || undefined,
        next_review_due_at: moduleNextReviewDueAt || undefined,
      })
      setNotice('Module draft created')
      setModuleTitle('')
      setModuleCode('')
      setModuleStandards('')
      setModuleDomainTags('')
      setModuleCadenceTags('annual')
      setModuleRoleApplicability('universal')
      setModuleTrackIds('')
      setModuleRecommendationTypes('training_only')
      setModulePolicyRefs('')
      setModuleLearningDepth('knowledge_attestation')
      setModuleDurationMinutes(15)
      setModuleAuthorName('')
      setModuleReviewerName('')
      setModuleApproverName('')
      setModuleClinicalAccuracyScope('')
      setModuleLastReviewedAt(toDateInput(new Date().toISOString()))
      setModuleNextReviewDueAt(toDateInput(new Date(Date.now() + 365 * 86400000).toISOString()))
      setModuleBlocks([{ type: 'text', text: '' }])
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to create module')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleGenerateQuizDraft() {
    if (!selectedModuleId) {
      setError('Select a module to generate a quiz draft')
      return
    }
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await generateQuizDraft(selectedModuleId, quizPrompt)
      setNotice('Quiz draft generated and queued for reviewer approval')
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to generate quiz draft')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleGenerateRecommendationDraft() {
    if (!selectedModuleId) {
      setError('Select a module before generating recommendations')
      return
    }
    const standards = recommendationStandards.split(',').map((token) => token.trim()).filter(Boolean)
    if (standards.length === 0) {
      setError('Provide at least one standard ID')
      return
    }
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await generateRecommendationDraft(selectedModuleId, recommendationPrompt, standards)
      setNotice('Draft recommendations generated')
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to generate recommendations')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleApproveReviewItem(entityType: 'module' | 'asset' | 'quiz', entityId: string, queueKey: string) {
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await approveReviewerQueueItem(entityType, entityId)
      setReviewDecisionAction((current) => ({ ...current, [queueKey]: 'approved' }))
      setNotice(`${entityType} approved with governance trail`)
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : `Failed to approve ${entityType}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleRejectReviewItem(entityType: 'module' | 'asset' | 'quiz', entityId: string, queueKey: string) {
    const reviewComment = (reviewDecisionComments[queueKey] || '').trim()
    if (!reviewComment) {
      setError('Reviewer comment is required for rejection')
      return
    }
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await rejectReviewerQueueItem(entityType, entityId, reviewComment)
      setReviewDecisionAction((current) => ({ ...current, [queueKey]: 'rejected' }))
      setNotice(`${entityType} rejected with reviewer rationale`)
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : `Failed to reject ${entityType}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleApproveRecommendation(recommendationId: string) {
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await approveRecommendation(recommendationId)
      setNotice('Recommendation approved')
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to approve recommendation')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleCreateAssignment() {
    if (!assignmentModuleId) {
      setError('Select a module for assignment')
      return
    }
    const resolvedAssignee = assignmentScope === 'individual'
      ? assignmentUserOptions.find((user) => {
        const searchValue = assignmentAssignee.trim().toLowerCase()
        const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ').toLowerCase()
        return user.user_id === assignmentAssignee.trim()
          || user.email.toLowerCase() === searchValue
          || displayName === searchValue
      })?.user_id || assignmentAssignee.trim() || undefined
      : undefined
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await createTrainingAssignment({
        module_id: assignmentModuleId,
        assignment_scope: assignmentScope,
        due_date: assignmentDueDate,
        target_role: assignmentScope === 'role' ? assignmentTargetRole : undefined,
        assignee_user_id: resolvedAssignee,
      })
      setNotice('Assignment created')
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to create assignment')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleCreateTrackDeployment() {
    if (!selectedTrackId) {
      setError('Select a track to deploy')
      return
    }
    if (trackDeploymentScope === 'facility' && !trackDeploymentFacilityId.trim()) {
      setError('Facility deployments require a facility id')
      return
    }
    if (trackDeploymentScope === 'role' && !trackDeploymentTargetRole.trim()) {
      setError('Role deployments require a target role')
      return
    }
    if (trackDeploymentScope === 'individual' && !trackDeploymentAssignee.trim()) {
      setError('Individual deployments require an assignee user id')
      return
    }
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      const deployment = await createTrainingTrackDeployment(selectedTrackId, {
        deployment_scope: trackDeploymentScope,
        due_date: trackDeploymentDueDate,
        facility_id: trackDeploymentScope === 'facility' ? trackDeploymentFacilityId.trim() : undefined,
        target_role: trackDeploymentScope === 'role' ? trackDeploymentTargetRole.trim() : undefined,
        assignee_user_id: trackDeploymentScope === 'individual' ? trackDeploymentAssignee.trim() : undefined,
        deployment_notes: trackDeploymentNotes.trim() || undefined,
      })
      setNotice(`Track deployment created with ${deployment.assignment_count} assignments`)
      setTrackDeploymentNotes('')
      if (trackDeploymentScope === 'individual') setTrackDeploymentAssignee('')
      await loadAllData()
      const refreshedTrack = await getTrainingTrackDetail(selectedTrackId)
      setSelectedTrackDetail(refreshedTrack)
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to deploy training track')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleApproveAndAssignRecommendation(recommendationId: string) {
    const dueDate = recommendationDueDates[recommendationId] || toDateInput(new Date(Date.now() + 14 * 86400000).toISOString())
    const remediationStrength = recommendationRemediationStrengths[recommendationId] || 'training_only'
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await approveRecommendation(recommendationId)
      await linkRecommendation({
        recommendation_id: recommendationId,
        remediation_strength: remediationStrength,
        create_assignment: true,
        due_date: dueDate,
        assignment_scope: 'role',
        target_role: 'user',
        assigned_by_scope: 'recommendation',
      })
      setNotice('Recommendation approved and assignment created')
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to approve and assign recommendation')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleRequestCompetencyValidation(assignmentId: string) {
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await requestCompetencyValidation({ assignment_id: assignmentId })
      setNotice('Competency validation requested')
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to request competency validation')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleReviewCompetencyValidation(validationId: string, decision: 'approved' | 'rejected') {
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await reviewCompetencyValidation({
        competency_validation_id: validationId,
        validation_status: decision,
        validation_notes: competencyReviewNotes[validationId] || undefined,
      })
      setNotice(`Competency validation ${decision}`)
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to review competency validation')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleSubmitQuiz() {
    if (!quizPlayerData || !selectedQuizId) return
    if (quizAnswers.some((answer) => answer === null)) {
      setError('Answer all quiz questions before submitting')
      return
    }
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      const result = await submitQuizAttempt(selectedQuizId, quizAnswers)
      if (quizPlayerData.quiz.module_id) await recordVideoEvent(quizPlayerData.quiz.module_id, 'view_completed')
      setLastAttemptResult(result)
      setAttemptHistory((current) => [
        {
          quiz_id: selectedQuizId,
          quiz_label: formatTrainingModuleTitle(quizPlayerData.quiz.education_modules, selectedQuizId),
          attempted_at: result.attempted_at,
          score_percent: result.score_percent,
          correct_count: result.correct_count,
          question_count: result.question_count,
          passed: result.passed,
          scoring_version: result.scoring_version,
        },
        ...current,
      ].slice(0, 12))
      setNotice(`Quiz submitted: ${result.score_percent}% (${result.correct_count}/${result.question_count}, ${result.passed ? 'Passed' : 'Failed'})`)
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to submit quiz attempt')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleRecordAssetProgress(moduleId: string, eventType: 'view_started' | 'view_completed') {
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await recordVideoEvent(moduleId, eventType)
      setNotice(eventType === 'view_completed' ? 'Asset completion logged' : 'Asset start logged')
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to record asset progress')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleMarkCompletion(assignmentId: string) {
    if (!learnerAttestations[assignmentId]) {
      setError('Attestation confirmation is required before recording completion')
      return
    }
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await createCompletion({ assignment_id: assignmentId, attestation_flag: true })
      const completedAt = new Date().toISOString()
      setAssignments((current) => current.map((assignment) => (
        assignment.assignment_id === assignmentId
          ? {
              ...assignment,
              assignment_state: 'completed',
              latest_completion: {
                completion_id: assignment.latest_completion?.completion_id || `local-${assignmentId}`,
                completed_at: assignment.latest_completion?.completed_at || completedAt,
                expires_at: assignment.latest_completion?.expires_at ?? null,
                attestation_flag: true,
                completion_depth: assignment.latest_completion?.completion_depth ?? null,
                evidence_package_id: assignment.latest_completion?.evidence_package_id ?? null,
              },
            }
          : assignment
      )))
      setLearnerAttestations((current) => ({ ...current, [assignmentId]: false }))
      setNotice('Completion recorded with attestation')
      await loadAllData()
      setAssignments((current) => current.map((assignment) => (
        assignment.assignment_id === assignmentId && !assignment.latest_completion
          ? {
              ...assignment,
              assignment_state: 'completed',
              latest_completion: {
                completion_id: `local-${assignmentId}`,
                completed_at: completedAt,
                expires_at: null,
                attestation_flag: true,
                completion_depth: null,
                evidence_package_id: null,
              },
            }
          : assignment
      )))
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to record completion')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleExport(redacted: boolean) {
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await requestTrainingExport(redacted, redacted ? undefined : 'Beta validation workflow')
      setNotice(redacted ? 'Redacted export generated' : 'Unredacted export request submitted for approval')
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to request export')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleGenerateEvidencePackage(completionId: string, redacted: boolean) {
    setIsSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      const evidencePackage = await generateEvidencePackage(completionId, redacted)
      setSelectedEvidencePackage(evidencePackage)
      setNotice(`Evidence package ${evidencePackage.evidence_package_id} generated`)
      await loadAllData()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to generate evidence package')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleLoadEvidencePackage(completionId: string) {
    setIsEvidenceLoading(true)
    setError(null)
    try {
      const evidencePackage = await getEvidencePackage(completionId)
      setSelectedEvidencePackage(evidencePackage)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load evidence package')
    } finally {
      setIsEvidenceLoading(false)
    }
  }

  function setAnswer(index: number, selectedOption: number) {
    const updated = [...quizAnswers]
    updated[index] = selectedOption
    setQuizAnswers(updated)
  }

  function updateModuleBlock(index: number, value: string) {
    setModuleBlocks((current) => current.map((block, blockIndex) => (blockIndex === index ? { ...block, text: value } : block)))
  }

  function addModuleBlock() {
    setModuleBlocks((current) => [...current, { type: 'text', text: '' }])
  }

  function removeModuleBlock(index: number) {
    setModuleBlocks((current) => (current.length === 1 ? current : current.filter((_, blockIndex) => blockIndex !== index)))
  }

  const transcriptModuleOptions = useMemo(() => {
    const labels = new Set<string>()
    for (const row of transcript) {
      const label = formatTrainingModuleTitle(row.training_assignments?.education_modules, '')
      if (label) labels.add(label)
    }
    return Array.from(labels).sort((a, b) => a.localeCompare(b))
  }, [transcript])

  const transcriptTrackOptions = useMemo(() => {
    const options = new Map<string, string>()
    for (const row of transcript) {
      const deployment = row.deployment_context
      if (!deployment?.track_id) continue
      options.set(deployment.track_id, deployment.track_title || deployment.track_key || deployment.track_id)
    }
    return Array.from(options.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [transcript])

  const transcriptDeploymentOptions = useMemo(() => {
    const options = new Map<string, string>()
    for (const row of transcript) {
      const deployment = row.deployment_context
      if (!deployment?.deployment_id) continue
      const labelBase = deployment.track_title || deployment.track_key || deployment.deployment_id
      const label = `${labelBase} deployment`
      options.set(deployment.deployment_id, label)
    }
    return Array.from(options.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [transcript])

  const filteredTranscript = useMemo(() => transcript.filter((row) => {
    const expired = isExpired(row.expires_at)
    const statusMatch = transcriptStatusFilter === 'completed' ? true : transcriptStatusFilter === 'open' ? !expired : expired
    const moduleName = formatTrainingModuleTitle(row.training_assignments?.education_modules, '')
    const moduleMatch = !transcriptModuleFilter || moduleName === transcriptModuleFilter
    const trackMatch = !transcriptTrackFilter || row.deployment_context?.track_id === transcriptTrackFilter
    const deploymentMatch = !transcriptDeploymentFilter || row.deployment_context?.deployment_id === transcriptDeploymentFilter
    return statusMatch && moduleMatch && trackMatch && deploymentMatch
  }), [transcript, transcriptDeploymentFilter, transcriptModuleFilter, transcriptStatusFilter, transcriptTrackFilter])

  const transcriptRollup = useMemo(() => {
    const assignmentIds = new Set<string>()
    const deploymentIds = new Set<string>()
    let evidenceCount = 0
    let capaLinkedCount = 0
    let recommendationLinkedCount = 0

    for (const row of filteredTranscript) {
      assignmentIds.add(row.assignment_id)
      if (row.evidence_package_id) evidenceCount += 1
      if (row.training_assignments?.linked_capa_id || row.recommendation_link?.linked_capa_id) capaLinkedCount += 1
      if (row.recommendation_link?.recommendation_id || row.training_assignments?.recommendation_id) recommendationLinkedCount += 1
      if (row.deployment_context?.deployment_id) deploymentIds.add(row.deployment_context.deployment_id)
    }

    let assignmentCount = assignmentIds.size
    if (transcriptDeploymentFilter) {
      const selectedDeployment = transcript.find((row) => row.deployment_context?.deployment_id === transcriptDeploymentFilter)?.deployment_context
      if (selectedDeployment?.assignment_count) assignmentCount = selectedDeployment.assignment_count
    } else if (transcriptTrackFilter) {
      const trackDeploymentCounts = new Map<string, number>()
      for (const row of transcript) {
        const deployment = row.deployment_context
        if (!deployment?.track_id || !deployment.deployment_id) continue
        if (trackDeploymentCounts.has(deployment.deployment_id)) continue
        if (deployment.track_id === transcriptTrackFilter) trackDeploymentCounts.set(deployment.deployment_id, deployment.assignment_count || 0)
      }
      if (trackDeploymentCounts.size > 0) {
        assignmentCount = Array.from(trackDeploymentCounts.values()).reduce((total, count) => total + count, 0)
      }
    }

    return {
      assignmentCount,
      completionCount: filteredTranscript.length,
      evidenceCount,
      capaLinkedCount,
      recommendationLinkedCount,
      deploymentCount: deploymentIds.size,
      completionRate: assignmentCount > 0 ? Math.round((filteredTranscript.length / assignmentCount) * 100) : 0,
    }
  }, [filteredTranscript, transcript, transcriptDeploymentFilter, transcriptTrackFilter])

  const dashboardFacilityOptions = useMemo(() => {
    const values = new Set<string>()
    for (const assignment of assignments) if (assignment.facility_id) values.add(assignment.facility_id)
    return Array.from(values).sort((a, b) => a.localeCompare(b))
  }, [assignments])

  const dashboardRoleOptions = useMemo(() => {
    const values = new Set<string>()
    for (const assignment of assignments) if (assignment.target_role) values.add(assignment.target_role)
    return Array.from(values).sort((a, b) => a.localeCompare(b))
  }, [assignments])

  const dashboardMetrics = useMemo(() => {
    const filteredAssignments = assignments.filter((assignment) => {
      const facilityMatch = !dashboardFacilityFilter || assignment.facility_id === dashboardFacilityFilter
      const roleMatch = !dashboardRoleFilter || assignment.target_role === dashboardRoleFilter
      return facilityMatch && roleMatch
    })
    const filteredAssignmentIds = new Set(filteredAssignments.map((assignment) => assignment.assignment_id))
    const filteredCompletions = transcript.filter((row) => filteredAssignmentIds.has(row.assignment_id))
    const completedAssignmentIds = new Set(filteredCompletions.map((row) => row.assignment_id))
    const now = Date.now()
    const in30Days = now + 30 * 86400000
    const overdueAssignments = filteredAssignments.filter((assignment) => new Date(`${assignment.due_date}T23:59:59.999Z`).getTime() < now && !completedAssignmentIds.has(assignment.assignment_id)).length
    const expiringSoon = filteredCompletions.filter((row) => {
      const expiresAt = new Date(row.expires_at).getTime()
      return expiresAt >= now && expiresAt <= in30Days
    }).length
    return {
      assignmentCount: filteredAssignments.length,
      completionCount: filteredCompletions.length,
      overdueAssignments,
      expiringSoon,
      completionRate: filteredAssignments.length > 0 ? Math.round((completedAssignmentIds.size / filteredAssignments.length) * 100) : 0,
    }
  }, [assignments, dashboardFacilityFilter, dashboardRoleFilter, transcript])

  const learnerProgressSummary = useMemo(() => {
    const assignmentIds = new Set(assignments.map((assignment) => assignment.assignment_id))
    const assignmentCompletionMap = new Map(
      transcript
        .filter((row) => assignmentIds.has(row.assignment_id))
        .map((row) => [row.assignment_id, row]),
    )
    let attestedCount = 0
    let assetCompletionCount = 0
    let validationPendingCount = 0

    for (const assignment of assignments) {
      if (assignment.latest_completion?.attestation_flag || assignmentCompletionMap.get(assignment.assignment_id)?.attestation_flag) {
        attestedCount += 1
      }
      if ((assignment.learner_asset_progress?.completed_count || 0) > 0) {
        assetCompletionCount += 1
      }
      const validation = competencyValidations.find((row) => row.assignment_id === assignment.assignment_id)
      if (validation?.validation_status === 'pending') {
        validationPendingCount += 1
      }
    }

    return {
      assignedCount: assignments.length,
      completedCount: assignmentCompletionMap.size,
      attestedCount,
      validationPendingCount,
      assetCompletionCount,
    }
  }, [assignments, competencyValidations, transcript])

  const currentQuizQuestion = quizPlayerData?.questions[quizQuestionIndex]

  return {
    isLoading,
    isSubmitting,
    error,
    notice,
    modules,
    assignments,
    reviewQueue,
    recommendations,
    competencyValidations,
    tracks,
    selectedTrackId,
    setSelectedTrackId,
    selectedTrackDetail,
    approvedQuizzes,
    transcript,
    adminOverview,
    videoSummary,
    moduleTitle,
    setModuleTitle,
    moduleCode,
    setModuleCode,
    moduleStandards,
    setModuleStandards,
    moduleDomainTags,
    setModuleDomainTags,
    moduleCadenceTags,
    setModuleCadenceTags,
    moduleRoleApplicability,
    setModuleRoleApplicability,
    moduleTrackIds,
    setModuleTrackIds,
    moduleRecommendationTypes,
    setModuleRecommendationTypes,
    modulePolicyRefs,
    setModulePolicyRefs,
    moduleLearningDepth,
    setModuleLearningDepth,
    moduleDurationMinutes,
    setModuleDurationMinutes,
    moduleAuthorName,
    setModuleAuthorName,
    moduleReviewerName,
    setModuleReviewerName,
    moduleApproverName,
    setModuleApproverName,
    moduleClinicalAccuracyScope,
    setModuleClinicalAccuracyScope,
    moduleLastReviewedAt,
    setModuleLastReviewedAt,
    moduleNextReviewDueAt,
    setModuleNextReviewDueAt,
    moduleBlocks,
    moduleExpiryDays,
    setModuleExpiryDays,
    quizPrompt,
    setQuizPrompt,
    recommendationPrompt,
    setRecommendationPrompt,
    recommendationStandards,
    setRecommendationStandards,
    selectedModuleId,
    setSelectedModuleId,
    selectedModuleDetail,
    assignmentModuleId,
    setAssignmentModuleId,
    assignmentScope,
    setAssignmentScope,
    assignmentDueDate,
    setAssignmentDueDate,
    assignmentTargetRole,
    setAssignmentTargetRole,
    assignmentAssignee,
    setAssignmentAssignee,
    assignmentUserOptions,
    trackDeploymentScope,
    setTrackDeploymentScope,
    trackDeploymentDueDate,
    setTrackDeploymentDueDate,
    trackDeploymentFacilityId,
    setTrackDeploymentFacilityId,
    trackDeploymentTargetRole,
    setTrackDeploymentTargetRole,
    trackDeploymentAssignee,
    setTrackDeploymentAssignee,
    trackDeploymentNotes,
    setTrackDeploymentNotes,
    selectedQuizId,
    setSelectedQuizId,
    quizQuestionIndex,
    setQuizQuestionIndex,
    quizAnswers,
    quizPlayerData,
    lastAttemptResult,
    attemptHistory,
    quizLoadError,
    reviewDecisionComments,
    setReviewDecisionComment: (queueKey, value) => setReviewDecisionComments((current) => ({ ...current, [queueKey]: value })),
    reviewDecisionAction,
    transcriptStatusFilter,
    setTranscriptStatusFilter,
    transcriptModuleFilter,
    setTranscriptModuleFilter,
    transcriptTrackFilter,
    setTranscriptTrackFilter,
    transcriptDeploymentFilter,
    setTranscriptDeploymentFilter,
    dashboardFacilityFilter,
    setDashboardFacilityFilter,
    dashboardRoleFilter,
    setDashboardRoleFilter,
    selectedEvidencePackage,
    isEvidenceLoading,
    learnerAttestations,
    setLearnerAttestation: (assignmentId, value) => setLearnerAttestations((current) => ({ ...current, [assignmentId]: value })),
    librarySearch,
    setLibrarySearch,
    libraryStatusFilter,
    setLibraryStatusFilter,
    libraryDepthFilter,
    setLibraryDepthFilter,
    libraryDomainFilter,
    setLibraryDomainFilter,
    libraryTrackFilter,
    setLibraryTrackFilter,
    recommendationDueDates,
    recommendationRemediationStrengths,
    competencyReviewNotes,
    setCompetencyReviewNote: (validationId, value) => setCompetencyReviewNotes((current) => ({ ...current, [validationId]: value })),
    setRecommendationDueDate: (recommendationId, value) => setRecommendationDueDates((current) => ({ ...current, [recommendationId]: value })),
    setRecommendationRemediationStrength: (recommendationId, value) => setRecommendationRemediationStrengths((current) => ({ ...current, [recommendationId]: value })),
    moduleOptions,
    libraryDomainOptions,
    libraryTrackOptions,
    filteredModules,
    libraryTrackSummary,
    transcriptModuleOptions,
    transcriptTrackOptions,
    transcriptDeploymentOptions,
    dashboardFacilityOptions,
    dashboardRoleOptions,
    filteredTranscript,
    transcriptRollup,
    dashboardMetrics,
    learnerProgressSummary,
    showEmptyTrainingState: !isLoading && !error && modules.length === 0 && assignments.length === 0 && reviewQueue.length === 0 && approvedQuizzes.length === 0 && transcript.length === 0 && recommendations.length === 0,
    currentQuizQuestion,
    loadAllData,
    handleCreateModule,
    handleGenerateQuizDraft,
    handleGenerateRecommendationDraft,
    handleApproveReviewItem,
    handleRejectReviewItem,
    handleApproveRecommendation,
    handleApproveAndAssignRecommendation,
    handleRequestCompetencyValidation,
    handleReviewCompetencyValidation,
    handleCreateAssignment,
    handleCreateTrackDeployment,
    handleRecordAssetProgress,
    handleSubmitQuiz,
    handleMarkCompletion,
    handleExport,
    handleGenerateEvidencePackage,
    handleLoadEvidencePackage,
    clearEvidenceSelection: () => setSelectedEvidencePackage(null),
    setAnswer,
    updateModuleBlock,
    addModuleBlock,
    removeModuleBlock,
  }
}

function SectionButton({ active, icon, label, count, onClick }: { active: boolean; icon: ReactNode; label: string; count?: number; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={active ? 'training-nav-item training-nav-item-active' : 'training-nav-item'}>
      <span className="flex items-center gap-2">
        <span className="text-[var(--brand-primary)]">{icon}</span>
        <span>{label}</span>
      </span>
      {typeof count === 'number' ? <span className="training-count-chip">{count}</span> : null}
    </button>
  )
}

export function TrainingAssignmentsPage() {
  const { viewerContext } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const section = readSectionFromPath(location.pathname)
  const isAdmin = viewerContext?.role === 'Admin'
  const isReviewer = viewerContext?.role === 'Reviewer'
  const isStaff = viewerContext?.role === 'Staff'
  const isMultiFacilityTier =
    viewerContext?.tier === 'consultant' ||
    (viewerContext?.tier === 'enterprise' &&
      viewerContext.facility_scope_status !== 'unknown' &&
      viewerContext.facility_scope_status !== 'none' &&
      (viewerContext.facility_ids.length ?? 0) !== 1)

  const baseNavigation = [
    { id: 'overview', allowed: !isStaff },
    { id: 'library', allowed: isAdmin },
    { id: 'tracks', allowed: !isStaff },
    { id: 'assignments', allowed: isAdmin },
    { id: 'review', allowed: isAdmin || isReviewer },
    { id: 'learner', allowed: true },
    { id: 'transcript', allowed: true },
  ].filter((item) => item.allowed) as Array<{ id: TrainingSection; allowed: boolean }>

  const allowedSections = new Set(baseNavigation.map((item) => item.id))
  const resolvedSection = allowedSections.has(section) ? section : getDefaultSectionForViewer(viewerContext?.role)
  const data = useTrainingWorkspaceData(resolvedSection, viewerContext?.role)

  function goToSection(nextSection: TrainingSection) {
    navigate(`/training/${nextSection}`)
  }

  const navigation = [
    { id: 'overview', label: isMultiFacilityTier ? 'Portfolio' : 'Overview', icon: <Sparkles size={16} />, count: undefined, allowed: !isStaff },
    { id: 'library', label: 'Library', icon: <BookOpen size={16} />, count: data.modules.length, allowed: isAdmin },
    { id: 'tracks', label: 'Tracks', icon: <Video size={16} />, count: data.tracks.length, allowed: !isStaff },
    { id: 'assignments', label: isMultiFacilityTier ? 'Deployment' : 'Assignments', icon: <ClipboardList size={16} />, count: data.assignments.length, allowed: isAdmin },
    { id: 'review', label: 'Review', icon: <ShieldCheck size={16} />, count: data.reviewQueue.length + data.competencyValidations.filter((validation) => validation.validation_status === 'pending').length, allowed: isAdmin || isReviewer },
    { id: 'learner', label: 'My Learning', icon: <GraduationCap size={16} />, count: data.learnerProgressSummary.assignedCount, allowed: true },
    { id: 'transcript', label: 'Evidence', icon: <FileCheck size={16} />, count: data.filteredTranscript.length, allowed: true },
  ].filter((item) => item.allowed) as Array<{ id: TrainingSection; label: string; icon: ReactNode; count?: number }>

  useEffect(() => {
    if (resolvedSection !== section) navigate(`/training/${resolvedSection}`, { replace: true })
  }, [navigate, resolvedSection, section])

  let content: ReactNode
  if (resolvedSection === 'library') content = <LibrarySection data={data} />
  else if (resolvedSection === 'tracks') content = <TracksSection data={data} />
  else if (resolvedSection === 'assignments') content = <AssignmentsSection data={data} />
  else if (resolvedSection === 'review') content = <ReviewSection data={data} />
  else if (resolvedSection === 'learner') content = <LearnerSection data={data} />
  else if (resolvedSection === 'transcript') content = <TranscriptSection data={data} />
  else content = <OverviewSection data={data} />
  const hasWorkspaceContent = data.modules.length > 0
    || data.assignments.length > 0
    || data.reviewQueue.length > 0
    || data.approvedQuizzes.length > 0
    || data.transcript.length > 0
    || data.competencyValidations.length > 0
    || data.tracks.length > 0

  return (
    <div className="training-workspace">
      <section className="training-topbar">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.28em] text-amber-300">ASC Survey</p>
            <p className="font-brand text-sm font-bold text-white">Training Platform V1</p>
          </div>
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-[248px_minmax(0,1fr)]">
        <aside className="training-sidebar">
          <div>
            <p className="training-eyebrow">Workspace</p>
            <h2 className="mt-1 font-brand text-lg font-bold text-[var(--brand-deep)]">Training menu</h2>
          </div>
          <div className="relative mt-5">
            <select
              aria-label="Training menu"
              value={resolvedSection}
              onChange={(event) => goToSection(event.target.value as TrainingSection)}
              className="training-input w-full appearance-none pr-9 font-bold"
            >
              {navigation.map((item) => (
                <option key={item.id} value={item.id}>
                  {typeof item.count === 'number' ? `${item.label} (${item.count})` : item.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
          </div>
          <div className="mt-3 space-y-2">
            {navigation.map((item) => (
              <SectionButton key={item.id} active={resolvedSection === item.id} icon={item.icon} label={item.label} count={item.count} onClick={() => goToSection(item.id)} />
            ))}
          </div>
          <div className="mt-6 rounded-3xl border border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-4">
            <p className="training-eyebrow">Training actions</p>
            <p className="mt-2 text-sm text-slate-600">Review open assignments, launch approved lessons, and keep evidence current for your facility.</p>
            {canShowAnes008MediaProof() ? (
              <button type="button" className="btn-ghost mt-3 w-full px-3 py-2 text-xs" onClick={() => navigate('/training/media/anes-008')}>
                ANES-008 media proof
              </button>
            ) : null}
            <button type="button" className="btn-ghost mt-2 w-full px-3 py-2 text-xs" onClick={() => navigate('/training/media/med-005')}>
              MED-005 media proof
            </button>
          </div>
        </aside>

        <main className="space-y-5">
          {data.error ? <section className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{data.error}</section> : null}
          {data.notice ? <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{data.notice}</section> : null}
          {data.isLoading ? <section className="training-panel p-4 text-sm text-slate-600">Loading training data...</section> : null}
          {data.showEmptyTrainingState ? (
            <section className="training-panel border border-dashed border-slate-200 p-4 text-sm text-slate-600">
              No training modules or assignments yet. Create the first module draft or assignment to start the training workspace.
            </section>
          ) : null}
          {!data.isLoading || hasWorkspaceContent ? content : null}
        </main>
      </div>
    </div>
  )
}
